import * as rotulos from '../../bureaus.rotulos';
import { BUREAU_TAMANHO_PAGINA, IBureauTenantResumo } from '../../models/bureaus.model';
import {
    IBureauEquipeMembro,
    IBureauEquipeVinculoMembro,
} from '../../models/bureaus-equipes.model';
import { MeurhBureausEquipesService } from '../../bureaus-equipes.service';
import { ModalConfirmService } from '../../../../../shared/components/modal-confirm/modal.confirm.service';

export class MeurhBureausEquipesMembrosController {
    static $inject = [
        'MeurhBureausEquipesService',
        'ModalConfirmService',
        'NewToaster',
    ];

    public administradorBureaus: boolean;
    /** Tenants que o administrador de equipe administra, carregados pela tela. Vazio para o administrador de Bureaus. */
    public tenantsLimite: IBureauTenantResumo[];
    /** Muda depois de uma inclusão pelo botão do título da tela. */
    public recarga: number;
    /** Avisa a tela quando a API responde 403. */
    public aoSemPermissao: () => void;
    public membros: IBureauEquipeMembro[] = [];
    /** Total de pessoas, do `full_count` da primeira página. */
    public totalLista = 0;
    /** A última página veio incompleta: não há mais pessoas a carregar. */
    public fim = true;
    public carregandoMais = false;
    public filtro = '';
    /**
     * Tenants do limite que cada membro ainda não atende, indexados por `bureauusuario`.
     * Calculados uma vez por carga, para o `ui-select` não receber um array novo a cada digest.
     */
    public tenantsDisponiveis: Record<string, IBureauTenantResumo[]> = {};
    /** Campo "ID do tenant" do administrador de Bureaus, indexado por `bureauusuario`. */
    public novoTenantId: Record<string, number | null> = {};
    /** Seleção do administrador de equipe, indexada por `bureauusuario`. */
    public novoTenantSelecionado: Record<string, number | null> = {};
    public vinculando: Record<string, boolean> = {};

    constructor(
        public meurhBureausEquipesService: MeurhBureausEquipesService,
        private modalConfirmService: ModalConfirmService,
        private NewToaster: any,
    ) { }

    public $onInit(): void {
        this.carregar();
    }

    public $onChanges(changes: angular.IOnChangesObject): void {
        if (changes.tenantsLimite) {
            this.calcularTenantsDisponiveis();
        }

        if (changes.recarga && !changes.recarga.isFirstChange()) {
            this.carregar();
        }
    }

    public carregar(): void {
        this.carregandoMais = false;

        this.meurhBureausEquipesService
            .getMembros(this.filtro)
            .then((membros) => {
                this.membros = membros || [];
                this.totalLista = this.membros.length > 0 ? this.membros[0].full_count || 0 : 0;
                this.fim = this.membros.length < BUREAU_TAMANHO_PAGINA;
                this.calcularTenantsDisponiveis();
            })
            .catch((error) => {
                this.tratarErroCarga(error);
            });
    }

    public carregarMais(): void {
        const ultimo = this.membros[this.membros.length - 1];
        this.carregandoMais = true;

        this.meurhBureausEquipesService
            .getMembros(this.filtro, { created_at: ultimo.created_at, bureauusuario: ultimo.bureauusuario })
            .then((membros) => {
                this.membros = this.membros.concat(membros || []);
                this.fim = (membros || []).length < BUREAU_TAMANHO_PAGINA;
                this.calcularTenantsDisponiveis();
            })
            .catch((error) => {
                this.tratarErroCarga(error);
            })
            .finally(() => {
                this.carregandoMais = false;
            });
    }

    public buscar(): void {
        this.carregar();
    }

    public total(): number {
        return this.totalLista;
    }

    /** Também é chamado pelo `ui-select` antes de haver seleção. */
    public rotuloTenant(tenant: IBureauTenantResumo): string {
        return tenant ? rotulos.rotuloTenant(tenant) : '';
    }

    public nomePessoa(membro: IBureauEquipeMembro): string {
        return rotulos.nomePessoa(membro);
    }

    public podeVincular(membro: IBureauEquipeMembro): boolean {
        return this.tenantParaVincular(membro) !== null;
    }

    public vincularTenant(membro: IBureauEquipeMembro): void {
        const tenantId = this.tenantParaVincular(membro);

        if (tenantId === null) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Erro ao vincular tenant',
                body: 'Informe um ID de tenant válido.'
            });
            return;
        }

        this.vinculando[membro.bureauusuario] = true;

        this.meurhBureausEquipesService
            .createMembroTenant(membro.bureauusuario, tenantId)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tenant vinculado com sucesso!'
                });

                this.novoTenantId[membro.bureauusuario] = null;
                this.novoTenantSelecionado[membro.bureauusuario] = null;
                this.carregar();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao vincular tenant',
                    body: error?.data?.message || 'Ocorreu um erro ao vincular o tenant.'
                });
            })
            .finally(() => {
                this.vinculando[membro.bureauusuario] = false;
            });
    }

    public removerTenant(membro: IBureauEquipeMembro, vinculo: IBureauEquipeVinculoMembro): void {
        const nome = this.nomePessoa(membro);
        const tenant = this.rotuloTenant(vinculo.tenant);
        const ultimo = membro.tenants.length === 1;

        const modal = this.modalConfirmService.open(
            vinculo,
            'tenant',
            true,
            {
                title: 'Remover tenant do membro',
                subtitle: ultimo
                    ? `Você está prestes a remover o tenant ${tenant}, o último de ${nome} nesta lista.`
                    : `Você está prestes a remover o tenant ${tenant} de ${nome}.`,
                confirmText: ultimo
                    ? `Ao removê-lo, ${nome} deixa de aparecer aqui. Tem certeza que deseja remover?`
                    : 'Tem certeza que deseja remover este tenant?',
                closeButton: 'Cancelar',
                confirmButton: 'Sim, remover',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarRemocaoTenant(membro, vinculo);
            })
            .catch(() => undefined);
    }

    public removerMembro(membro: IBureauEquipeMembro): void {
        const nome = this.nomePessoa(membro);

        const modal = this.modalConfirmService.open(
            membro,
            'membro',
            true,
            {
                title: 'Remover membro de equipe',
                subtitle: this.administradorBureaus
                    ? `Você está prestes a remover ${nome} de todos os tenants que atende.`
                    : `Você está prestes a remover ${nome} dos tenants que você administra.`,
                confirmText: 'Tem certeza que deseja remover?',
                closeButton: 'Cancelar',
                confirmButton: 'Sim, remover',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarRemocaoMembro(membro);
            })
            .catch(() => undefined);
    }

    private calcularTenantsDisponiveis(): void {
        if (this.administradorBureaus) {
            return;
        }

        const tenantsLimite = this.tenantsLimite || [];
        const disponiveis: Record<string, IBureauTenantResumo[]> = {};

        this.membros.forEach((membro) => {
            const atendidos = membro.tenants.map((vinculo) => vinculo.tenant.tenant);

            disponiveis[membro.bureauusuario] = tenantsLimite.filter((tenant) => atendidos.indexOf(tenant.tenant) === -1);
        });

        this.tenantsDisponiveis = disponiveis;
    }

    private executarRemocaoTenant(membro: IBureauEquipeMembro, vinculo: IBureauEquipeVinculoMembro): void {
        this.meurhBureausEquipesService
            .deleteMembroTenant(membro.bureauusuario, vinculo.bureauequipemembro)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tenant removido com sucesso!'
                });

                this.carregar();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao remover tenant',
                    body: error?.data?.message || 'Ocorreu um erro ao remover o tenant.'
                });
            });
    }

    private executarRemocaoMembro(membro: IBureauEquipeMembro): void {
        this.meurhBureausEquipesService
            .deleteMembro(membro.bureauusuario)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Membro de equipe removido com sucesso!'
                });

                this.carregar();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao remover membro',
                    body: error?.data?.message || 'Ocorreu um erro ao remover o membro de equipe.'
                });
            });
    }

    /** O backend valida `tenant_id` com `is_int()`, então só um inteiro positivo pode ser enviado. */
    private tenantParaVincular(membro: IBureauEquipeMembro): number | null {
        const valor: any = this.administradorBureaus
            ? this.novoTenantId[membro.bureauusuario]
            : this.novoTenantSelecionado[membro.bureauusuario];

        if (valor === null || valor === undefined || valor === '') {
            return null;
        }

        const tenantId = parseInt(valor, 10);

        return Number.isInteger(tenantId) && tenantId > 0 ? tenantId : null;
    }

    private tratarErroCarga(error: any): void {
        if (error?.xhrStatus === 'abort') {
            return;
        }

        if (error?.status === 403) {
            this.aoSemPermissao();
            return;
        }

        this.NewToaster.pop({
            type: 'error',
            title: 'Erro ao carregar membros de equipe',
            body: error?.data?.message || 'Ocorreu um erro ao carregar os membros de equipe.'
        });
    }
}
